import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Search, Package } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { toast } from "@/components/ui/use-toast";

interface Product {
  id: string;
  code: string;
  name: string;
  current_stock: number;
  min_stock: number;
  max_stock: number;
  unit: string;
  category_name: string;
}

export default function ProductSearch() {
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState<Product[]>([]);

  const handleSearch = async () => {
    if (!searchTerm.trim()) {
      toast({
        title: "Erro",
        description: "Digite um código ou nome de produto para pesquisar",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('products')
        .select(`
          id,
          code,
          name,
          current_stock,
          min_stock,
          max_stock,
          unit,
          categories (
            name
          )
        `)
        .or(`code.ilike.%${searchTerm}%,name.ilike.%${searchTerm}%`)
        .order('name');

      if (error) throw error;

      const formattedProducts = data.map(product => ({
        ...product,
        category_name: product.categories?.name || 'N/A'
      }));

      setProducts(formattedProducts);

      if (data?.length === 0) {
        toast({
          title: "Nenhum resultado",
          description: "Nenhum produto encontrado com esse código ou nome",
        });
      }
    } catch (error: any) {
      console.error('Erro ao pesquisar produtos:', error);
      toast({
        title: "Erro",
        description: "Não foi possível realizar a pesquisa",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const getStockStatus = (current: number, min: number, max: number) => {
    if (current <= min) {
      return { color: 'text-red-600', text: 'Baixo', bgColor: 'bg-red-50' };
    } else if (current >= max) {
      return { color: 'text-yellow-600', text: 'Alto', bgColor: 'bg-yellow-50' };
    } else {
      return { color: 'text-green-600', text: 'Normal', bgColor: 'bg-green-50' };
    }
  };

  return (
    <div className="container mx-auto py-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Consulta de Produtos</h1>
      </div>

      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="text-lg font-medium">Pesquisar Produto</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-4">
            <Input
              placeholder="Digite o código ou nome do produto..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              className="max-w-md"
            />
            <Button onClick={handleSearch} disabled={loading}>
              {loading ? (
                "Pesquisando..."
              ) : (
                <>
                  <Search className="h-4 w-4 mr-2" />
                  Pesquisar
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {products.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg font-medium">Resultados da Pesquisa</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Código</TableHead>
                  <TableHead>Nome</TableHead>
                  <TableHead>Categoria</TableHead>
                  <TableHead>Estoque Atual</TableHead>
                  <TableHead>Estoque Mínimo</TableHead>
                  <TableHead>Estoque Máximo</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Unidade</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {products.map((product) => {
                  const status = getStockStatus(
                    product.current_stock,
                    product.min_stock,
                    product.max_stock
                  );
                  return (
                    <TableRow key={product.id}>
                      <TableCell className="font-medium">{product.code}</TableCell>
                      <TableCell>{product.name}</TableCell>
                      <TableCell>{product.category_name}</TableCell>
                      <TableCell>{product.current_stock}</TableCell>
                      <TableCell>{product.min_stock}</TableCell>
                      <TableCell>{product.max_stock}</TableCell>
                      <TableCell>
                        <span className={`px-2 py-1 rounded-full text-sm ${status.bgColor} ${status.color}`}>
                          {status.text}
                        </span>
                      </TableCell>
                      <TableCell>{product.unit}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
